import { supabase } from '../utils/supabase';

/**
 * Get player ID from auth user ID
 * @param {string} authUserId - Auth user ID
 * @returns {Promise<string|null>} Player ID or null if not found
 */
const getPlayerIdFromAuthId = async (authUserId) => {
  if (!authUserId) return null;
  
  const { data, error } = await supabase
    .from('players')
    .select('id')
    .eq('auth_id', authUserId)
    .single();
    
  if (error) {
    console.error('Error getting player ID from auth ID:', error);
    return null;
  }
  
  return data?.id || null;
};

/**
 * Add a context link to a game
 * @param {Object} contextData - Context data
 * @param {string} contextData.gameId - Game ID
 * @param {string} contextData.userId - Auth user ID (will be converted to player ID)
 * @param {string} contextData.url - URL link
 * @param {string} [contextData.description] - Optional description
 * @param {string} [contextData.visibility] - Visibility level ('private', 'game', 'club', 'public')
 * @param {string} [contextData.sourceType] - Source type ('youtube', 'google_drive', etc.)
 * @returns {Promise<Object>} Created context
 */
export const addGameContext = async (contextData) => {
  // Convert auth user ID to player ID
  const playerId = await getPlayerIdFromAuthId(contextData.userId);
  
  if (!playerId) {
    throw new Error('Player not found. Please ensure you are logged in with a valid account.');
  }
  
  const { data, error } = await supabase
    .from('game_contexts')
    .insert([{
      game_id: contextData.gameId,
      user_id: playerId, // Use player ID, not auth ID
      url: contextData.url,
      description: contextData.description || null,
      visibility: contextData.visibility || 'private',
      source_type: contextData.sourceType || 'other'
    }])
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

/**
 * Get all visible contexts for a game
 * @param {string} gameId - Game ID
 * @param {string} authUserId - Current auth user ID (will be converted to player ID for filtering)
 * @param {string} [clubId] - Club ID (for club visibility)
 * @param {Array} [gameParticipantIds] - Array of player IDs who participated in the game
 * @returns {Promise<Array>} Array of visible contexts
 */
export const getGameContexts = async (gameId, authUserId, clubId = null, gameParticipantIds = []) => {
  // Convert auth user ID to player ID for filtering
  const playerId = await getPlayerIdFromAuthId(authUserId);
  
  // First, get all contexts for the game
  const { data: allContexts, error } = await supabase
    .from('game_contexts')
    .select(`
      *,
      user:user_id (
        id,
        first_name,
        last_name
      )
    `)
    .eq('game_id', gameId)
    .order('created_at', { ascending: false });
    
  if (error) throw error;
  if (!allContexts) return [];
  
  // Filter by visibility
  const visibleContexts = allContexts.filter(context => {
    // Owner can always see their own links (compare player IDs)
    if (playerId && context.user_id === playerId) return true;
    
    switch (context.visibility) {
      case 'private':
        return false; // Only owner can see
      case 'public':
        return true; // Any authenticated user can see
      case 'game':
        // All game participants can see
        return playerId ? gameParticipantIds.includes(playerId) : false;
      case 'club':
        // All club members can see (if clubId is provided)
        return clubId !== null;
      default:
        return false;
    }
  });
  
  return visibleContexts;
};

/**
 * Update a game context (only by owner)
 * @param {string} contextId - Context ID
 * @param {string} authUserId - Auth user ID (will be converted to player ID)
 * @param {Object} updates - Fields to update (camelCase will be converted to snake_case)
 * @returns {Promise<Object>} Updated context
 */
export const updateGameContext = async (contextId, authUserId, updates) => {
  // Convert auth user ID to player ID
  const playerId = await getPlayerIdFromAuthId(authUserId);
  
  if (!playerId) {
    throw new Error('Player not found. Please ensure you are logged in with a valid account.');
  }
  
  // First verify ownership
  const { data: existing, error: fetchError } = await supabase
    .from('game_contexts')
    .select('user_id')
    .eq('id', contextId)
    .single();
    
  if (fetchError) throw fetchError;
  if (existing.user_id !== playerId) {
    throw new Error('Only the owner can update this context');
  }
  
  // Convert camelCase to snake_case for database columns
  const dbUpdates = {};
  if (updates.url !== undefined) dbUpdates.url = updates.url;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.visibility !== undefined) dbUpdates.visibility = updates.visibility;
  if (updates.sourceType !== undefined) dbUpdates.source_type = updates.sourceType;
  
  const { data, error } = await supabase
    .from('game_contexts')
    .update(dbUpdates)
    .eq('id', contextId)
    .select()
    .single();
    
  if (error) throw error;
  return data;
};

/**
 * Delete a game context (only by owner)
 * @param {string} contextId - Context ID
 * @param {string} authUserId - Auth user ID (will be converted to player ID)
 * @returns {Promise<void>}
 */
export const deleteGameContext = async (contextId, authUserId) => {
  // Convert auth user ID to player ID
  const playerId = await getPlayerIdFromAuthId(authUserId);
  
  if (!playerId) {
    throw new Error('Player not found. Please ensure you are logged in with a valid account.');
  }
  
  // First verify ownership
  const { data: existing, error: fetchError } = await supabase
    .from('game_contexts')
    .select('user_id')
    .eq('id', contextId)
    .single();
    
  if (fetchError) throw fetchError;
  if (existing.user_id !== playerId) {
    throw new Error('Only the owner can delete this context');
  }
  
  const { error } = await supabase
    .from('game_contexts')
    .delete()
    .eq('id', contextId);
    
  if (error) throw error;
};

/**
 * Get count of visible contexts for a game (lightweight, for badges)
 * @param {string} gameId - Game ID
 * @param {string} authUserId - Current auth user ID (will be converted to player ID for filtering)
 * @param {string} [clubId] - Club ID (for club visibility)
 * @param {Array} [gameParticipantIds] - Array of player IDs who participated in the game
 * @returns {Promise<number>} Count of visible contexts
 */
export const getGameContextsCount = async (gameId, authUserId, clubId = null, gameParticipantIds = []) => {
  // Just get the visible contexts and return the count
  const contexts = await getGameContexts(gameId, authUserId, clubId, gameParticipantIds);
  return contexts.length;
};

/**
 * Batch fetch counts of visible contexts for multiple games (optimized for performance)
 * @param {Array<string>} gameIds - Array of game IDs
 * @param {string} authUserId - Current auth user ID (will be converted to player ID for filtering)
 * @param {string} [clubId] - Club ID (for club visibility)
 * @param {Object} [gameParticipantsMap] - Map of gameId -> array of participant player IDs
 * @returns {Promise<Object>} Object mapping gameId -> count of visible contexts
 */
export const getGameContextsCountsBatch = async (gameIds, authUserId, clubId = null, gameParticipantsMap = {}) => {
  if (!gameIds || gameIds.length === 0) return {};
  
  // Convert auth user ID to player ID once (instead of per-game)
  const playerId = await getPlayerIdFromAuthId(authUserId);
  
  // Fetch all contexts for all games in one query
  const { data: allContexts, error } = await supabase
    .from('game_contexts')
    .select('game_id, user_id, visibility')
    .in('game_id', gameIds);
    
  if (error) throw error;
  if (!allContexts) return {};
  
  // Group contexts by game_id
  const contextsByGame = {};
  allContexts.forEach(context => {
    if (!contextsByGame[context.game_id]) {
      contextsByGame[context.game_id] = [];
    }
    contextsByGame[context.game_id].push(context);
  });
  
  // Filter by visibility for each game and count
  const counts = {};
  gameIds.forEach(gameId => {
    const contexts = contextsByGame[gameId] || [];
    const gameParticipantIds = gameParticipantsMap[gameId] || [];
    
    const visibleCount = contexts.filter(context => {
      // Owner can always see their own links
      if (playerId && context.user_id === playerId) return true;
      
      switch (context.visibility) {
        case 'private':
          return false; // Only owner can see
        case 'public':
          return true; // Any authenticated user can see
        case 'game':
          // All game participants can see
          return playerId ? gameParticipantIds.includes(playerId) : false;
        case 'club':
          // All club members can see (if clubId is provided)
          return clubId !== null;
        default:
          return false;
      }
    }).length;
    
    counts[gameId] = visibleCount;
  });
  
  return counts;
};

